import React from 'react';
import { Helmet } from 'react-helmet-async';
import { AlternateLink, getAlternateLinksForPath } from '../data/localization';

const SITE_URL = 'https://financialmetrics.report';
const SITE_NAME = 'FinancialMetrics.report';
const DEFAULT_IMAGE = `${SITE_URL}/featured_image.png`;
const DEFAULT_DESCRIPTION =
  'FinancialMetrics.report provides localized financial calculators and guides for the United States, Canada, the United Kingdom, and Europe covering tax, salary, VAT, mortgage, debt, savings, investing, and business finance scenarios.';

type JsonLdValue = Record<string, unknown> | Array<Record<string, unknown>>;

interface SEOProps {
  title: string;
  description?: string;
  path?: string;
  canonicalUrl?: string;
  image?: string;
  type?: 'website' | 'article';
  robots?: string;
  publishedTime?: string;
  modifiedTime?: string;
  jsonLd?: JsonLdValue[];
  alternateLinks?: AlternateLink[];
  prevUrl?: string;
  nextUrl?: string;
}

export const absoluteUrl = (value?: string) => {
  if (!value) return undefined;
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  return `${SITE_URL}${value.startsWith('/') ? value : `/${value}`}`;
};

export const canonicalForPath = (path = '/') => {
  const cleanPath = path.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
  return `${SITE_URL}${cleanPath}`;
};

export const pageTitle = (title: string) =>
  title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;

export const orgJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/favicon.ico`,
  sameAs: [
    SITE_URL,
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'editorial corrections and support',
    email: 'info@financialmetrics.report',
    availableLanguage: ['English'],
  },
};

export const siteJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/guides?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

export const breadcrumbJsonLd = (items: Array<{ name: string; url: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: absoluteUrl(item.url),
  })),
});

export const faqJsonLd = (faqs?: Array<{ question: string; answer: string }>) => {
  if (!faqs?.length) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
};

export const SEO: React.FC<SEOProps> = ({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '/',
  canonicalUrl,
  image = DEFAULT_IMAGE,
  type = 'website',
  robots = 'index, follow, max-image-preview:large',
  publishedTime,
  modifiedTime,
  jsonLd = [],
  alternateLinks,
  prevUrl,
  nextUrl,
}) => {
  const canonical = canonicalUrl || canonicalForPath(path);
  const fullImage = absoluteUrl(image) || DEFAULT_IMAGE;
  const filteredJsonLd = jsonLd.filter(Boolean);
  const resolvedAlternateLinks = alternateLinks || getAlternateLinksForPath(path);
  const prevHref = absoluteUrl(prevUrl);
  const nextHref = absoluteUrl(nextUrl);

  return (
    <Helmet>
      <title>{pageTitle(title)}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonical} />
      {prevHref && <link rel="prev" href={prevHref} />}
      {nextHref && <link rel="next" href={nextHref} />}
      {resolvedAlternateLinks.map((link) => {
        const href = absoluteUrl(link.href);
        return href ? (
          <link key={`${link.hrefLang}-${href}`} rel="alternate" hrefLang={link.hrefLang} href={href} />
        ) : null;
      })}

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={fullImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />

      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

      {filteredJsonLd.map((entry, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(entry)}
        </script>
      ))}
    </Helmet>
  );
};
